/**
 * cro-collection.js — CRO collection page behaviour.
 * Native Shopify filters + Section Rendering API: every filter/sort/load-more fetches only this
 * section's HTML. The page works without this file (chips are links, the filter form is a GET).
 */
import {
  $,
  $$,
  openSheet,
  closeSheet,
  fetchSection,
  initQuickAdd,
  refreshWishlist,
  replaceUrl,
  track,
} from '@theme/cro-core';

const root = $('.cro-col');
if (root) init(root);

function init(root) {
  const sectionId = root.dataset.section;
  const grid = () => $('[data-cro-grid]', root);
  const filterSheet = document.getElementById(`filter-${sectionId}`);
  const sortSheet = document.getElementById(`sort-${sectionId}`);
  let pages = 1;
  let loading = false;
  let changedInSheet = false;

  /* Shopify's size filter matches any style that has the size, sold out or not. When the
     Availability filter exists (Search & Discovery), a picked size always travels with
     filter.v.availability=1 so only styles with that size in stock come back. */
  const sizeParam = root.dataset.sizeParam || 'filter.v.option.size';
  const normalize = (u) => {
    if (root.dataset.avail !== '1') return u;
    if (u.searchParams.has(sizeParam)) u.searchParams.set('filter.v.availability', '1');
    else u.searchParams.delete('filter.v.availability');
    return u;
  };

  /* ---------------- apply filters / sort */
  async function apply(url, { keepSheet = false } = {}) {
    const u = normalize(new URL(url, location.href));
    u.searchParams.delete('page');
    u.searchParams.delete('section_id');
    if (keepSheet) changedInSheet = true;
    grid().classList.add('is-loading');
    try {
      const doc = await fetchSection(u, sectionId);
      swap(doc);
      pages = 1;
      replaceUrl(u.pathname + u.search);
      if (!keepSheet) window.scrollTo({ top: 0, behavior: 'smooth' });
      track('cro_filter', { url: u.search });
    } catch (e) {
      location.href = u.toString();
    } finally {
      grid().classList.remove('is-loading');
    }
  }

  function swap(doc) {
    const pick = (sel) => doc.querySelector(sel);
    const newGrid = pick('[data-cro-grid]');
    if (newGrid) grid().replaceWith(newGrid);
    [['[data-cro-chips]'], ['[data-cro-countline]'], ['[data-cro-more]'], ['[data-cro-filters]']].forEach(([sel]) => {
      const fresh = pick(sel);
      const old = $(sel, root);
      if (!fresh || !old) return;
      if (sel === '[data-cro-filters]') {
        // keep exactly the group the shopper is working in open (one at a time)
        const open = new Set($$('details.cro-fgroup[open]', old).map((d) => d.dataset.key));
        $$('details.cro-fgroup', fresh).forEach((d) => (d.open = open.has(d.dataset.key)));
      }
      old.replaceWith(fresh);
    });
    if (filterSheet) $$('details.cro-fgroup[open]', filterSheet).forEach(clamp);
    const count = pick('[data-cro-count]');
    if (count) $$('[data-cro-count]', root).forEach((el) => (el.textContent = count.textContent));
    const n = pick('[data-cro-active-n]');
    const nOld = $('[data-cro-active-n]', root);
    if (n && nOld) {
      nOld.textContent = n.textContent;
      nOld.hidden = n.hidden;
    }
    focusGroup();
    observeMore();
    refreshWishlist();
  }

  /* ---------------- quick filter pills: the filter sheet, showing only the pill's filter */
  const sheetTitle = filterSheet && $('.cro-sheet__title', filterSheet);
  const fullTitle = sheetTitle ? sheetTitle.textContent : 'Filter';
  function focusGroup() {
    if (!filterSheet) return;
    const key = filterSheet.dataset.focus;
    $$('.cro-fgroup', filterSheet).forEach((d) => {
      const on = !!key && d.dataset.key === key;
      d.classList.toggle('is-focus', on);
      if (on) {
        d.open = true;
        clamp(d);
      }
    });
  }

  /* ---------------- filter groups: the full sheet opens with only Size expanded (Color when the
     collection has no size filter, as on most Haute Sauce collections); opening a group
     closes the others (details[name] does it natively in new browsers, this covers the rest);
     lists longer than 6 rows show 5 rows + "See more" (never hiding a ticked value). */
  const expanded = new Set();
  function clamp(d) {
    const vals = $('[data-cro-fvals]', d);
    const more = $('[data-cro-fmore]', d);
    if (!vals || !more) return;
    const all = () => {
      vals.classList.remove('is-clamp');
      more.hidden = true;
    };
    if (expanded.has(d.dataset.key)) return all();
    vals.classList.add('is-clamp');
    const chips = Array.from(vals.children);
    const tops = [...new Set(chips.map((c) => c.offsetTop))];
    const hidden = chips.filter((c) => tops.indexOf(c.offsetTop) > 4);
    if (tops.length <= 6 || hidden.some((c) => c.querySelector('input:checked'))) return all();
    more.hidden = false;
    more.textContent = `See more (${hidden.length})`;
  }
  filterSheet?.addEventListener('cro:open', () => {
    if (filterSheet.dataset.focus) return;
    const groups = $$('details.cro-fgroup', filterSheet);
    const first = groups.find((d) => d.dataset.key === sizeParam) || groups.find((d) => /\.colou?r$/.test(d.dataset.key));
    groups.forEach((d) => (d.open = d === first));
    $$('details.cro-fgroup[open]', filterSheet).forEach(clamp);
  });
  filterSheet?.addEventListener(
    'toggle',
    (e) => {
      const d = e.target;
      if (!d.classList || !d.classList.contains('cro-fgroup') || !d.open) return;
      if (!filterSheet.dataset.focus) $$('details.cro-fgroup[open]', filterSheet).forEach((x) => x !== d && (x.open = false));
      clamp(d);
    },
    true
  );
  filterSheet?.addEventListener('click', (e) => {
    const b = e.target.closest('[data-cro-fmore]');
    if (!b) return;
    const d = b.closest('details.cro-fgroup');
    expanded.add(d.dataset.key);
    clamp(d);
    track('cro_filter_more', { filter: d.dataset.key });
  });
  root.addEventListener('click', (e) => {
    const pill = e.target.closest('[data-cro-qf]');
    if (!pill || !filterSheet) return;
    e.preventDefault();
    const key = pill.dataset.croQf;
    const group = $$('.cro-fgroup', filterSheet).find((d) => d.dataset.key === key);
    if (!group) return openSheet(filterSheet);
    filterSheet.dataset.focus = key;
    if (sheetTitle) sheetTitle.textContent = group.dataset.label || fullTitle;
    focusGroup();
    openSheet(filterSheet);
    track('cro_quick_filter_open', { filter: key });
  });
  filterSheet?.addEventListener('cro:close', () => {
    delete filterSheet.dataset.focus;
    if (sheetTitle) sheetTitle.textContent = fullTitle;
    focusGroup();
    if (changedInSheet) {
      changedInSheet = false;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });

  // Collection links: show the tapped one as picked straight away (the next page takes a moment)
  root.addEventListener('click', (e) => {
    const link = e.target.closest('.cro-col__cats a');
    if (!link) return;
    $$('.cro-col__cats a.is-on', root).forEach((x) => x.classList.remove('is-on'));
    link.classList.add('is-on');
    track('cro_collection_link', { to: link.getAttribute('href') });
  });

  // Chips and "clear" links
  root.addEventListener('click', (e) => {
    const a = e.target.closest('a[data-cro-filter-link]');
    if (!a) return;
    e.preventDefault();
    const d = a.closest('dialog');
    // "Clear" inside a pill's sheet clears just that filter and keeps the sheet open
    if (d && d === filterSheet && filterSheet.dataset.focus && a.hasAttribute('data-cro-clear')) {
      const g = $('.cro-fgroup.is-focus', filterSheet);
      const form = $('[data-cro-filter-form]', root);
      if (g && form) {
        $$('input', g).forEach((i) => (i.type === 'checkbox' ? (i.checked = false) : (i.value = '')));
        clearTimeout(t);
        apply(formUrl(form), { keepSheet: true });
        return;
      }
    }
    if (d && a.hasAttribute('data-cro-close-after')) closeSheet(d);
    apply(a.href);
  });

  // Filter sheet: live-apply on change so the grid behind the sheet is already right
  let t;
  const formUrl = (form) => {
    const u = new URL(form.action, location.href);
    new FormData(form).forEach((v, k) => {
      if (String(v).trim() !== '') u.searchParams.append(k, v);
    });
    return u;
  };
  root.addEventListener('change', (e) => {
    const form = e.target.closest('[data-cro-filter-form]');
    if (form) {
      clearTimeout(t);
      t = setTimeout(() => apply(formUrl(form), { keepSheet: true }), e.target.type === 'number' ? 700 : 150);
      return;
    }
    const sort = e.target.closest('[data-cro-sort-form]');
    if (sort) {
      const u = new URL(location.href);
      u.searchParams.set('sort_by', e.target.value);
      closeSheet(sortSheet);
      apply(u);
    }
  });
  root.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-cro-filter-form]');
    if (!form) return;
    e.preventDefault();
    clearTimeout(t);
    const u = normalize(formUrl(form));
    const same = u.pathname + u.search === location.pathname + location.search;
    closeSheet(filterSheet);
    // Live-apply already brought these results in behind the sheet: just close it.
    if (!same) apply(u);
  });

  /* ---------------- infinite scroll */
  let io;
  function observeMore() {
    if (io) io.disconnect();
    const more = $('[data-cro-more]', root);
    if (!more || !$('[data-cro-next]', more) || !('IntersectionObserver' in window)) return;
    io = new IntersectionObserver((entries) => entries.some((en) => en.isIntersecting) && loadMore(), {
      rootMargin: '0px 0px 1400px 0px',
    });
    io.observe(more);
  }
  async function loadMore() {
    const more = $('[data-cro-more]', root);
    const next = more && $('[data-cro-next]', more);
    if (!next || loading) return false;
    loading = true;
    more.classList.add('is-loading');
    try {
      const doc = await fetchSection(next.href, sectionId);
      const g = grid();
      const have = new Set($$('.cro-card', g).map((c) => c.dataset.pid));
      const frag = document.createDocumentFragment();
      $$('[data-cro-grid] > .cro-card', doc).forEach((c) => {
        if (have.has(c.dataset.pid)) return;
        $$('img', c).forEach((i) => (i.loading = 'lazy'));
        frag.appendChild(c);
      });
      g.appendChild(frag);
      const freshMore = doc.querySelector('[data-cro-more]');
      if (freshMore) more.replaceWith(freshMore);
      pages++;
      refreshWishlist();
      observeMore();
      return true;
    } catch (e) {
      more.classList.remove('is-loading');
      return false;
    } finally {
      loading = false;
    }
  }
  root.addEventListener('click', (e) => {
    const next = e.target.closest('[data-cro-next]');
    if (!next) return;
    e.preventDefault();
    loadMore();
  });
  observeMore();

  /* ---------------- come back from a product to the same place */
  const KEY = 'cro-col-state';
  const save = () => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify({ k: location.pathname + location.search, n: pages, y: window.scrollY }));
    } catch (e) {}
  };
  root.addEventListener('click', (e) => e.target.closest('a[href*="/products/"]') && save(), true);
  window.addEventListener('pagehide', save);
  (async () => {
    const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (!nav || nav.type !== 'back_forward') return;
    let st;
    try {
      st = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    } catch (e) {}
    if (!st || st.k !== location.pathname + location.search || st.n < 2) return;
    history.scrollRestoration = 'manual';
    for (let i = 1; i < Math.min(st.n, 6); i++) {
      if (!(await loadMore())) break;
    }
    window.scrollTo(0, st.y);
  })();

  /* ---------------- ?pin= products that were not on page 1 */
  (async () => {
    const pin = window.__croPin;
    if (!pin || !pin.missing || !pin.missing.length) return;
    const cards = await Promise.all(
      pin.missing.map(async (key) => {
        try {
          const path = /^\d+$/.test(key) ? null : `/products/${encodeURIComponent(key)}`;
          if (!path) return null;
          const doc = await fetchSection(path, 'cro-card');
          const src = doc.querySelector('[data-cro-pin-src]');
          if (!src || src.dataset.available !== 'true') return null;
          return src.querySelector('.cro-card');
        } catch (e) {
          return null;
        }
      })
    );
    const g = grid();
    // insert after the pins already moved to the top, in the order given
    let after = null;
    pin.keys.forEach((key) => {
      const existing = g.querySelector(`.cro-card[data-h="${CSS.escape(key)}"]`);
      if (existing && existing.querySelector('.cro-card__soldout')) return;
      const fetched = cards[pin.missing.indexOf(key)];
      const card = existing || fetched;
      if (!card) return;
      if (fetched) {
        const dup = g.querySelector(`.cro-card[data-pid="${fetched.dataset.pid}"]`);
        if (dup) dup.remove();
      }
      if (after) after.after(card);
      else g.prepend(card);
      after = card;
    });
    refreshWishlist();
  })();

  /* ---------------- floating Sort/Filter button: out of the way while scrolling down */
  const fab = $('[data-cro-fab]', root);
  if (fab) {
    let lastY = window.scrollY;
    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const y = window.scrollY;
          fab.classList.toggle('is-hidden', y > lastY + 4 && y > 400);
          if (y < lastY - 4 || y < 400) fab.classList.remove('is-hidden');
          lastY = y;
          ticking = false;
        });
      },
      { passive: true }
    );
  }

  /* ---------------- quick add */
  initQuickAdd(root, $('[data-cro-qa-sheet]', root));
  refreshWishlist();
}
